/**
 * Data Validator Utility
 * 
 * Handles validation of imported data against application rules
 * Provides detailed error reporting for each validation failure
 * 
 * Updated for PRD v8: Risk Sources Classification Update
 */

import { riskSourcesLevels, criticalityLevels, sensitiveDataTypes } from '../../config/assetFormConfig';

// Define placeholder values used for incomplete data
export const PLACEHOLDER_VALUES = {
  TEXT: '[To be updated]',
  EMAIL: 'update-required@placeholder.local'
};

// Define required fields
export const REQUIRED_FIELDS = [
  'assetName'  // Only basic information field is mandatory
];

// Define valid dropdown values - updated for Risk Sources
export const VALID_VALUES = {
  riskSources: riskSourcesLevels,
  criticalityClassification: criticalityLevels,
  
  // New simplified data classification
  containsSensitiveData: sensitiveDataTypes,
  
  // Streamlined key controls only (6 controls)
  ssoImplemented: ['Yes', 'No', 'N/A'],
  twoFactorAuth: ['Yes', 'No', 'N/A'],
  dataEncryption: ['Yes', 'No', 'N/A'],
  backupsImplemented: ['Yes', 'No', 'N/A'],
  networkSegmentation: ['Yes', 'No', 'N/A'],
  userAccessReviews: ['Yes', 'No', 'N/A'],
  
  // Legacy fields (for backward compatibility during import migration)
  securityClassification: ['Internal Use', 'Confidential', 'Highly Confidential'], // Legacy - for migration
  boardInfo: ['Yes', 'No', 'Unknown'],
  executiveInfo: ['Yes', 'No', 'Unknown'],
  financialInfo: ['Yes', 'No', 'Unknown'],
  otherConfidential: ['Yes', 'No', 'Unknown'],
  customerPII: ['Yes', 'No', 'Unknown'],
  employeePII: ['Yes', 'No', 'Unknown'],
  sensitivePII: ['Yes', 'No', 'Unknown'],
  rbacImplemented: ['Yes', 'No', 'N/A'],
  advancedEndpointProtection: ['Yes', 'No', 'N/A'],
  onboardedOntoSOC: ['Yes', 'No', 'N/A']
};

/**
 * Validate asset name with enhanced empty field handling
 * @param {string} assetName - Asset name to validate
 * @param {number} rowIndex - Row index for validation messages (1-based)
 * @param {Object} clientInfo - Optional client information for naming preview
 * @returns {Object} Validation result with errors and warnings
 */
const validateAssetName = (assetName, rowIndex, clientInfo = null) => {
  const errors = [];
  const warnings = [];
  
  if (!assetName || assetName.trim() === '') {
    // Don't create an error - let auto-generation handle it
    // But add a warning for user awareness
    const clientPrefix = clientInfo?.name ? `${clientInfo.name} - ` : '';
    const generatedName = `${clientPrefix}Asset ${rowIndex}`;
    
    warnings.push({
      row: rowIndex,
      field: 'assetName',
      type: 'auto_generated',
      message: `Asset name was empty and will be auto-generated as "${generatedName}"`,
      value: generatedName,
      suggestion: 'Consider providing meaningful asset names for better identification'
    });
    
    return { errors, warnings };
  }
  
  if (assetName.length < 2) {
    errors.push({
      row: rowIndex,
      field: 'assetName',
      type: 'length',
      message: 'Asset name must be at least 2 characters long',
      value: assetName,
      suggestion: 'Provide a meaningful name with at least 2 characters'
    });
  }
  
  if (assetName.length > 200) {
    warnings.push({
      row: rowIndex,
      field: 'assetName',
      type: 'length_warning',
      message: 'Asset name is very long (over 200 characters). This might indicate a data formatting issue.',
      value: assetName.substring(0, 50) + '...',
      suggestion: 'Consider using a shorter, more concise asset name'
    });
  }
  
  // Check for suspicious data patterns
  if (/test|sample|example|dummy|placeholder/i.test(assetName)) {
    warnings.push({
      row: rowIndex,
      field: 'assetName',
      type: 'data_quality',
      message: 'Asset name appears to be test/sample data',
      value: assetName,
      suggestion: 'Consider using actual asset names instead of test data'
    });
  }
  
  return { errors, warnings };
};

/**
 * Validate a single row of transformed data
 * @param {Object} rowData - Transformed row data
 * @param {number} rowIndex - Row index for error reporting (1-based)
 * @param {Object} clientInfo - Optional client information for enhanced validation
 * @returns {Object} Validation result with errors
 */
export const validateRow = (rowData, rowIndex, clientInfo = null) => {
  const errors = [];
  const warnings = [];
  
  // Flatten the structured data for validation
  const flatData = flattenRowData(rowData);
  
  // Enhanced asset name validation
  const assetNameValidation = validateAssetName(flatData.assetName, rowIndex, clientInfo);
  errors.push(...assetNameValidation.errors);
  warnings.push(...assetNameValidation.warnings);
  
  // Validate email format with better error message
  if (flatData.ownerEmail && 
      flatData.ownerEmail.trim() !== '' && 
      flatData.ownerEmail !== PLACEHOLDER_VALUES.EMAIL) {
    if (!isValidEmail(flatData.ownerEmail)) {
      errors.push({
        row: rowIndex,
        field: 'ownerEmail',
        type: 'format',
        message: `Invalid email format: "${flatData.ownerEmail}". Expected format: name@domain.com`,
        value: flatData.ownerEmail,
        suggestion: 'Ensure the email follows the format: username@domain.com'
      });
    }
  }
  
  // Validate dropdown values with enhanced matching and suggestions
  Object.entries(VALID_VALUES).forEach(([fieldName, validValues]) => {
    const value = flatData[fieldName];
    if (value && value.trim() !== '') {
      // Try to find a valid match (case-insensitive)
      const matchedValue = findValidValue(value, validValues);
      
      if (!matchedValue) {
        // No match found - generate helpful error with suggestion
        const suggestion = findClosestMatch(value, validValues);
        errors.push({
          row: rowIndex,
          field: fieldName,
          type: 'invalid_value',
          message: `Invalid value "${value}" for ${getFieldDisplayName(fieldName)}. Did you mean "${suggestion}"?`,
          value: value,
          validValues: validValues,
          suggestion: `Change "${value}" to "${suggestion}" or one of: ${validValues.join(', ')}`
        });
      }
      // Note: If we found a match, we don't need to do anything as the value 
      // correction should have already been applied in the dataMapper
    }
  });

  // Check for potential email issues
  if (flatData.ownerEmail && flatData.ownerEmail.includes(' ')) {
    warnings.push({
      row: rowIndex,
      field: 'ownerEmail',
      type: 'warning',
      message: 'Email address contains spaces, which may cause issues',
      value: flatData.ownerEmail,
      suggestion: 'Remove any spaces from the email address'
    });
  }

  // Note: Removed legacy business logic check for Risk Sources
  // since Risk Sources (Internal/External/Both) doesn't correlate with criticality levels
  
  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    rowIndex
  };
};

/**
 * Validate all imported data
 * @param {Array} transformedData - Array of transformed row data
 * @param {Object} clientInfo - Optional client information for enhanced validation
 * @returns {Object} Complete validation results
 */
export const validateImportData = (transformedData, clientInfo = null) => {
  const allErrors = [];
  const allWarnings = [];
  const validRows = [];
  const invalidRows = [];
  
  transformedData.forEach((rowData, index) => {
    const rowIndex = index + 1; // 1-based indexing for user display
    const validation = validateRow(rowData, rowIndex, clientInfo);
    
    if (validation.isValid) {
      validRows.push({
        index: rowIndex,
        data: rowData
      });
    } else {
      invalidRows.push({
        index: rowIndex,
        data: rowData,
        errors: validation.errors
      });
    }
    
    allErrors.push(...validation.errors);
    allWarnings.push(...validation.warnings);
  });
  
  // Group errors by type for summary
  const errorSummary = groupErrorsByType(allErrors);
  const warningSummary = groupErrorsByType(allWarnings);
  
  return {
    isValid: allErrors.length === 0,
    totalRows: transformedData.length,
    validRows: validRows.length,
    invalidRows: invalidRows.length,
    errors: allErrors,
    warnings: allWarnings,
    errorSummary,
    warningSummary,
    validRowsData: validRows,
    invalidRowsData: invalidRows
  };
};

/**
 * Flatten structured row data for validation
 * @param {Object} rowData - Structured row data
 * @returns {Object} Flattened data
 */
const flattenRowData = (rowData) => {
  const flat = {};
  
  // Basic Info
  if (rowData.basicInfo) {
    flat.assetName = rowData.basicInfo.name;
    flat.assetDescription = rowData.basicInfo.description;
    flat.assetLocation = rowData.basicInfo.location;
  }
  
  // Owner Info
  if (rowData.ownerInfo) {
    flat.ownerDepartment = rowData.ownerInfo.ownerDepartment;
    flat.ownerName = rowData.ownerInfo.ownerName;
    flat.ownerEmail = rowData.ownerInfo.ownerEmail;
  }
  
  // Security Info
  if (rowData.securityInfo) {
    flat.riskSources = rowData.securityInfo.riskSources;
    flat.criticalityClassification = rowData.securityInfo.criticalityClassification;
    flat.boardInfo = rowData.securityInfo.boardInfo;
    flat.executiveInfo = rowData.securityInfo.executiveInfo;
    flat.financialInfo = rowData.securityInfo.financialInfo;
    flat.otherConfidential = rowData.securityInfo.otherConfidential;
  }
  
  // Key Controls
  if (rowData.keyControls) {
    flat.ssoImplemented = rowData.keyControls.ssoImplemented;
    flat.rbacImplemented = rowData.keyControls.rbacImplemented;
    flat.twoFactorAuth = rowData.keyControls.twoFactorAuth;
    flat.networkSegmentation = rowData.keyControls.networkSegmentation;
    flat.dataEncryption = rowData.keyControls.dataEncryption;
    flat.userAccessReviews = rowData.keyControls.userAccessReviews;
    flat.advancedEndpointProtection = rowData.keyControls.advancedEndpointProtection;
    flat.onboardedOntoSOC = rowData.keyControls.onboardedOntoSOC;
    flat.backupsImplemented = rowData.keyControls.backupsImplemented;
  }
  
  // Data Info
  if (rowData.dataInfo) {
    flat.customerPII = rowData.dataInfo.customerPII;
    flat.employeePII = rowData.dataInfo.employeePII;
    flat.sensitivePII = rowData.dataInfo.sensitivePII;
  }
  
  return flat;
};

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {boolean} Whether email is valid
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Get display name for field
 * @param {string} fieldName - Internal field name
 * @returns {string} Display name
 */
const getFieldDisplayName = (fieldName) => {
  const displayNames = {
    assetName: 'Asset Name',
    assetDescription: 'Description',
    assetLocation: 'Location',
    ownerDepartment: 'Owner Department',
    ownerName: 'Owner Name',
    ownerEmail: 'Owner Email',
    riskSources: 'Risk Sources',
    criticalityClassification: 'Criticality Classification',
    boardInfo: 'Board Information',
    executiveInfo: 'Executive Information',
    financialInfo: 'Financial Information',
    otherConfidential: 'Other Confidential Information',
    customerPII: 'Customer PII',
    employeePII: 'Employee PII',
    sensitivePII: 'Sensitive Employee PII',
    ssoImplemented: 'SSO',
    rbacImplemented: 'RBAC',
    twoFactorAuth: '2FA',
    networkSegmentation: 'Network Segmentation',
    dataEncryption: 'Data Encryption',
    userAccessReviews: 'User Access Reviews',
    advancedEndpointProtection: 'Advanced Endpoint Protection',
    onboardedOntoSOC: 'Onboarded onto SOC',
    backupsImplemented: 'Backups',
    containsSensitiveData: 'Contains Sensitive Data'
  };
  
  return displayNames[fieldName] || fieldName;
};

/**
 * Group errors by type for summary
 * @param {Array} errors - Array of error objects
 * @returns {Object} Grouped errors
 */
const groupErrorsByType = (errors) => {
  const grouped = {
    required: [],
    format: [],
    invalid_value: [],
    length: [],
    warning: []
  };
  
  errors.forEach(error => {
    if (grouped[error.type]) {
      grouped[error.type].push(error);
    }
  });
  
  return grouped;
};

/**
 * Generate validation summary text
 * @param {Object} validationResult - Result from validateImportData
 * @returns {string} Summary text
 */
export const getValidationSummary = (validationResult) => {
  const { totalRows, validRows, invalidRows, errors, warnings } = validationResult;
  
  let summary = `Validation Results: ${validRows}/${totalRows} rows are valid for import`;
  
  if (invalidRows > 0) {
    summary += ` (${invalidRows} rows have validation errors)`;
  }
  
  if (errors.length > 0) {
    const requiredFieldErrors = errors.filter(e => e.type === 'required').length;
    if (requiredFieldErrors > 0) {
      summary += `\n• ${requiredFieldErrors} rows missing required Asset Name`;
    }
    const otherErrors = errors.length - requiredFieldErrors;
    if (otherErrors > 0) {
      summary += `\n• ${otherErrors} other validation errors found`;
    }
  }
  
  if (warnings.length > 0) {
    summary += `\n• ${warnings.length} data quality warnings found`;
  }
  
  if (validRows > 0) {
    summary += `\n\nNote: Missing owner information will be populated with placeholders and can be completed later.`;
    summary += `\n• Text fields: "${PLACEHOLDER_VALUES.TEXT}"`;
    summary += `\n• Email fields: "${PLACEHOLDER_VALUES.EMAIL}"`;
  }
  
  return summary;
};

/**
 * Check for potential duplicate assets
 * @param {Array} transformedData - Array of transformed row data
 * @returns {Array} Array of potential duplicates
 */
export const findPotentialDuplicates = (transformedData) => {
  const duplicates = [];
  const assetNames = new Map();
  
  transformedData.forEach((rowData, index) => {
    const assetName = rowData.basicInfo?.name?.toLowerCase().trim();
    if (assetName) {
      if (assetNames.has(assetName)) {
        const existingIndex = assetNames.get(assetName);
        duplicates.push({
          asset1: { index: existingIndex + 1, name: transformedData[existingIndex].basicInfo.name },
          asset2: { index: index + 1, name: rowData.basicInfo.name },
          reason: 'Identical asset names'
        });
      } else {
        assetNames.set(assetName, index);
      }
    }
  });
  
  return duplicates;
};

/**
 * Generate error report for download
 * @param {Object} validationResult - Result from validateImportData
 * @returns {string} CSV formatted error report
 */
export const generateErrorReport = (validationResult) => {
  const { errors, warnings } = validationResult;
  
  let report = 'Row,Field,Type,Message,Value,Suggestion\n';
  
  [...errors, ...warnings].forEach(item => {
    const row = item.row || '';
    const field = getFieldDisplayName(item.field) || '';
    const type = item.type || '';
    const message = (item.message || '').replace(/"/g, '""'); // Escape quotes
    const value = (item.value || '').replace(/"/g, '""'); // Escape quotes
    const suggestion = (item.suggestion || '').replace(/"/g, '""'); // Escape quotes
    
    report += `${row},"${field}","${type}","${message}","${value}","${suggestion}"\n`;
  });
  
  return report;
};

/**
 * Check if validation result allows import to proceed
 * @param {Object} validationResult - Result from validateImportData
 * @returns {boolean} Whether import can proceed
 */
export const canProceedWithImport = (validationResult) => {
  // Can proceed if there are no critical errors, even if there are warnings
  return validationResult.validRows > 0 && validationResult.errors.length === 0;
};

/**
 * Normalize value for comparison (case-insensitive, trimmed)
 * @param {string} value - Value to normalize
 * @returns {string} Normalized value
 */
const normalizeValue = (value) => {
  if (!value) return '';
  return String(value).trim().toLowerCase();
};

/**
 * Find valid value using case-insensitive and fuzzy matching
 * @param {string} inputValue - Input value to match
 * @param {Array} validValues - Array of valid values
 * @returns {string|null} Matched valid value or null
 */
const findValidValue = (inputValue, validValues) => {
  if (!inputValue || !validValues) return null;
  
  const normalizedInput = normalizeValue(inputValue);
  
  // First try exact match (case-insensitive)
  const exactMatch = validValues.find(v => 
    normalizeValue(v) === normalizedInput
  );
  if (exactMatch) return exactMatch;
  
  // Try partial matching for common variations
  const partialMatch = validValues.find(v => {
    const normalizedValid = normalizeValue(v);
    return normalizedValid.includes(normalizedInput) || 
           normalizedInput.includes(normalizedValid);
  });
  
  return partialMatch;
};

/**
 * Find closest match for suggestions using string similarity
 * @param {string} inputValue - Input value
 * @param {Array} validValues - Array of valid values  
 * @returns {string} Best matching valid value
 */
const findClosestMatch = (inputValue, validValues) => {
  if (!inputValue || !validValues || validValues.length === 0) {
    return validValues ? validValues[0] : '';
  }
  
  const input = normalizeValue(inputValue);
  
  // Find closest match using simple string similarity
  let bestMatch = validValues[0];
  let bestScore = 0;
  
  validValues.forEach(validValue => {
    const valid = normalizeValue(validValue);
    
    // Calculate similarity score
    let score = 0;
    if (valid.includes(input) || input.includes(valid)) {
      score = 0.8;
    } else {
      // Simple character overlap scoring
      const commonChars = [...input].filter(char => valid.includes(char)).length;
      score = commonChars / Math.max(input.length, valid.length);
    }
    
    if (score > bestScore) {
      bestScore = score;
      bestMatch = validValue;
    }
  });
  
  return bestMatch;
}; 